import React from "react";
import { Card } from "@/components/ui/card";
import { CategoryCopySource, getCategoryAboutContent } from "@/utils/categoryCopy";

interface CategorySeoContentProps {
  categoryName: string;
  content?: string | null;
  category?: CategoryCopySource;
}

/**
 * Renders long-form SEO body content for a category page.
 * Supports a minimal markdown subset: paragraphs and "## " H3 headings.
 * Renders nothing when no content is set.
 */
export const CategorySeoContent: React.FC<CategorySeoContentProps> = ({
  categoryName,
  content,
  category,
}) => {
  const resolvedContent = content?.trim() || (category ? getCategoryAboutContent(category) : "");
  if (!resolvedContent) return null;

  const blocks = resolvedContent
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <Card className="p-4 sm:p-6 border border-forum-border-subtle">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
        About {categoryName}
      </h2>
      <div className="prose prose-sm sm:prose-base max-w-none text-gray-700">
        {blocks.map((block, i) => {
          if (block.startsWith("## ")) {
            return (
              <h3
                key={i}
                className="text-base sm:text-lg font-semibold text-gray-900 mt-4 mb-2"
              >
                {block.replace(/^##\s+/, "")}
              </h3>
            );
          }
          if (block.startsWith("# ")) {
            return (
              <h3
                key={i}
                className="text-base sm:text-lg font-semibold text-gray-900 mt-4 mb-2"
              >
                {block.replace(/^#\s+/, "")}
              </h3>
            );
          }
          return (
            <p key={i} className="mb-3 leading-relaxed">
              {block}
            </p>
          );
        })}
      </div>
    </Card>
  );
};
